import configs from '../configs';

/**
 * Keycloak Configuration
 * Reads dynamically from configs, with default values as fallback
 */

export const KEYCLOAK_REALM_STORAGE_KEY = 'APP_AUTH_REALM';
export const KEYCLOAK_AUTO_LOGIN_HINT_KEY = 'KC_AUTO_LOGIN_AVAILABLE';

export const KeycloakRealm = {
  Internal: 'internal',
  External: 'external',
};

// Read realm names from configs, keeping default values
const realmNameMap = {
  [KeycloakRealm.Internal]: configs.keycloak_realm_internal || 'Ambev',
  [KeycloakRealm.External]: configs.keycloak_realm_external || 'Third',
};

export const getRedirectUri = (location = window.location) => {
  // Local development environment always uses localhost:3000
  if (location.host.indexOf('localhost') > -1) {
    return 'http://localhost:3000';
  }
  if (configs.env === 'prod') {
    return 'https://sensoryone.ab-inbev.com';
  }
  if (configs.env === 'qa') {
    return 'https://sensoryone-qa.ab-inbev.com';
  }
  return configs.defaultUrl || location.origin;
};

// Base config — read from configs, keeping default values
export const keycloakConfig = {
  url: configs.keycloak_url || 'https://kc-dev.ambevdevs.com.br',
  realm: configs.keycloak_realm_internal || 'Ambev',
  clientId: configs.keycloak_client_id || 'abi-gth-sensory-one',
};

// Backward compatibility: export environment object (used in tests)
export const environment = {
  keycloak: {
    url: keycloakConfig.url,
    realm: {
      internal: realmNameMap[KeycloakRealm.Internal],
      external: realmNameMap[KeycloakRealm.External],
    },
    clientId: keycloakConfig.clientId,
    redirectUri: getRedirectUri(),
  },
};

export const keycloakInitOptions = {
  onLoad: 'check-sso',
  redirectUri: getRedirectUri(),
  silentCheckSsoRedirectUri: `${getRedirectUri()}/silent-check-sso.html`,
  checkLoginIframe: false,
  pkceMethod: 'S256',
};

export const keycloakLoginOptions = {
  redirectUri: getRedirectUri(),
};

export const keycloakLogoutOptions = {
  redirectUri: getRedirectUri(),
};

/**
 * Resolve the user-selected realm from localStorage
 * Defaults to internal realm if not found
 */
export function resolveSelectedRealm() {
  const storedRealm = localStorage.getItem(KEYCLOAK_REALM_STORAGE_KEY);
  return storedRealm || KeycloakRealm.Internal;
}

/**
 * Get the complete Keycloak config object (used to create Keycloak instance)
 */
export function getKeycloakConfig(realm) {
  const realmKey = realm || resolveSelectedRealm();
  return {
    url: keycloakConfig.url,
    realm: realmNameMap[realmKey] || realmNameMap[KeycloakRealm.Internal],
    clientId: keycloakConfig.clientId,
  };
}

/**
 * Utility function to get the Token
 * Can be called from anywhere
 */
export const getKeycloakToken = () => {
  const keycloak = window.__keycloak;
  if (keycloak && keycloak.token) {
    return keycloak.token;
  }
  return null;
};

/**
 * Utility function to get user info
 */
export const getKeycloakUserInfo = () => {
  const keycloak = window.__keycloak;
  if (keycloak && keycloak.tokenParsed) {
    return {
      email: keycloak.tokenParsed.email,
      name: keycloak.tokenParsed.name,
      preferred_username: keycloak.tokenParsed.preferred_username,
      sub: keycloak.tokenParsed.sub,
      roles: keycloak.tokenParsed.realm_access?.roles || [],
    };
  }
  return null;
};
